import React, { useContext, useEffect, useState, useRef } from 'react';
import { useHistory, Link } from "react-router-dom";

import Chart from 'core/ui/charts/index';

import DualSelect from "partials/dual-select";

import SessionContext from 'SessionContext'
import {formatNumber} from "../core/ui/charts/chart-base";
import SearchDropDown from "../core/ui/search-drop-down";
import ComplexFilterBuilder from "./complex-filter-builder";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Row, Col, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, FormFeedback } from 'reactstrap';
import { existingComplexFilters, existingSimpleFilters } from 'core/utils/data-helper-functions';
import isEqual from 'lodash.isequal';
import { useGlobalState } from "hooks/useGlobalState";
import InfoPopover from "../core/ui/info-popover";

export const scoringPartitionOptions = [
    [10, "10 - Deciles"],
    [20, "20 - Ventiles"],
];

export default function ModelForm(props) {
    const session = useContext(SessionContext)
    const history = useHistory()
    const { setIsGlobalStateSaved } = useGlobalState();
    const [loaded, setLoaded] = useState(false)

    const [modelName, setModelName] = useState('')
    const [modelDescription, setModelDescription] = useState('')

    const [modelNameError, setModelNameError] = useState()
    const [modelDescriptionError, setModelDescriptionError] = useState()
    const [modelNameInvalid, setModelNameInvalid] = useState(false)
    const [modelDescInvalid, setModelDescInvalid] = useState(false)

    const [restrictGeography, setRestrictGeography] = useState( false)
    const [allFields] = useState(Object.values(session.chartsStore.modelable))
    const [excludedFields, setExcludedFields] = useState([])
    const [restrictAudienceId, setRestrictAudienceId] = useState('')
    const [restrictAudienceName, setRestrictAudienceName] = useState('')
    const [searchTerm, setSearchTerm] = useState('')
    const [negativeCount, setNegativeCount] = useState(-1)
    const [positiveCount, setPositiveCount] = useState(-1)
    const [scoringUniverse, setScoringUniverse] = useState(-1)
    const [scoringPartition, setScoringPartition] = useState(5)
    
    const [scoringPartitionLabel, setScoringPartitionLabel] = useState()
    const [scoringPartitionsIsOpen, setScoringPartitionsIsOpen] = useState(false);
    const [originalModel, setOriginalModel] = useState({});
    const [modelHasChanged, setModelHasChanged] = useState(false);
    const [hasMinPositiveCount, setHasMinPositiveCount] = useState(true);
    const [hasScrolled, setHasScrolled] = useState(false);
    let unsavedContinuePath = useRef(null);
    const targetModelId = useRef(props.args.model);
    const [refreshKey, setRefreshKey] = useState(0);

    const minimumPositiveRecords = window.min_model_positive_quantity || "500";
    const access = session.appState.userCan;

    const [isComplex, setIsComplex] = useState(session.crossfilterStore.isComplex);
    const [complexModalIsOpen, setComplexModalIsOpen] = useState(false)
    const [unsavedModalIsOpen, setUnsavedModalIsOpen] = useState(false);
    const [complexFilters, setComplexFilters] = useState();
    const [complexFields, setComplexFields] = useState();
    const [initialRender, setInitialRender] = useState(true);
    const [dataFieldsMissing, setDataFieldsMissing] = useState(false);
    const [dataFiltersHaveChanged, setDataFiltersHaveChanged] = useState(false);
    const [noFilters, setNoFilters] = useState(false);
    const [restrictedAudienceMissing, setRestrictedAudienceMissing] = useState(false);
    const [currentCompanyId, setCurrentCompanyId] = useState(false);
    const [activeFilters, setActiveFilters] = useState([]);

    function onModelNameChange(ev) {
        setModelName(ev.target.value)
    }
    function onModelDescriptionChange(ev) {
        setModelDescription(ev.target.value)
    }

    function checkForExistingRestrictedAucience(id) {
        session.query("audience/audiences_still_valid", {ids: [id]})
            .then((response) => {
                if (!response.length) return;
                const exists = response[0].is_active;
                setRestrictedAudienceMissing(!exists);
        });
    }

    useEffect(() => {
      if( props.args.model && Object.keys(originalModel).length ) {
        // it is saved - read from selected criteria
        let _ids = originalModel?.saved_selection_criteria.map(item => item.id);
        setActiveFilters(_ids);
      } else if( !props.args.model ) {
        // it is new - read from filters
        let _activeFilters = Object.entries(session.crossfilterStore.filters)
        .filter(([_, filters]) => !!filters)
        .map(([chart_id, _]) => chart_id);
        setActiveFilters(_activeFilters);
      }
    // eslint-disable-next-line
    }, [props.args.model, originalModel])

    useEffect(() => {
        if (targetModelId.current) {
            session.query('model/get', { id: targetModelId.current })
                .then((_model) => {
                    if (_model.status !== 'Created') {
                        history.push(`/model-details#model=${targetModelId.current}`)
                    }
                    setOriginalModel(JSON.parse(JSON.stringify(_model)));
                    setModelName(_model.name)
                    setModelDescription(_model.description)
                    setRestrictGeography(_model.restrict_geography);
                    setRestrictAudienceId(_model.filter_model_id || "");
                    setRestrictAudienceName(_model.filter_model_name);
                    // setSearchTerm(_model.filter_model_name);
                    setNegativeCount(_model.negative_count);
                    setPositiveCount(_model.positive_count);
                    setScoringUniverse(_model.scoring_universe);
                    setScoringPartition(_model.scoring_quantiles);
                    setIsComplex(_model.is_complex);
                    session.crossfilterStore.set_missing_filters(false);

                    // check if selected data fields are still available
                    if (_model && _model.excluded_fields.length) {
                        setDataFieldsMissing(checkDataFieldsExist(_model.excluded_fields));
                    }

                    // check restricted audience exists
                    checkForExistingRestrictedAucience(_model.filter_model_id);

                    // set excluded fields to fields that are available
                    const availableFields =
                        allFields.filter((a) =>
                            (_model.excluded_fields || []).find((f) => a.ds === f.ds && a.id === f.id )
                        )

                    availableFields && setExcludedFields(availableFields);

                    let availableFilters;
                    if (_model.is_complex) {
                        availableFilters = session.crossfilterStore.checkComplexFiltersExist(_model.complex_filters);
                        _model.complex_filters = availableFilters;
                        setComplexFilters(_model.complex_filters);
                        session.crossfilterStore.getComplexFields().then((_complexFields) => {
                            setComplexFields(_complexFields);
                        });
                    }

                    session.crossfilterStore.setFilters(_model.filters || {});
                    setLoaded(true)
                })
        }
        else {
            if (session.crossfilterStore.isComplex) {
                setIsComplex(session.crossfilterStore.isComplex);
                setComplexFilters(session.crossfilterStore.complexFilters);
                session.crossfilterStore.getComplexFields().then((_complexFields) => {
                    setComplexFields(_complexFields);
                });
            }
            setLoaded(true)
        }
        session.crossfilterStore.set_no_filters(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshKey]);

    // Scrolls the page down to the 'below minimum positive records' warning in the right column so it is seen.
    useEffect(() => { 
        const minPosWarning = document.getElementById("minPosCountWarning");
        if (!modelHasChanged && minPosWarning && loaded && !hasScrolled) {
            minPosWarning.scrollIntoView({ behavior: "smooth" });
            setHasScrolled(true);
        }
    }, [modelHasChanged, loaded, hasScrolled]);

    // This effect halts in app page router navigation to warn about unsaved data
    useEffect(() => {
        const unblock = history.block((location, action) => {
            // check if changes have been made and the location has changed. The location gets reset with a modal toggle. 
            if(currentCompanyId && (session.appState.activeCompany.id !== currentCompanyId)) {
              return true;
            } else if (modelHasChanged && unsavedContinuePath.current !== location.pathname) {
              unsavedContinuePath.current = location.pathname;
              setUnsavedModalIsOpen(true)
              return false;
            } else {
              return true;
            }
        });

        return () => {
          unblock();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [history, modelHasChanged]);

    // Unsaved Modal continue handler
    function continueWithoutSaving() {
        // use ref stored when modal was opened with current intended path
        history.push(unsavedContinuePath.current);
    }

    useEffect(() => { 
        let value = parseInt(scoringPartition);
        if(value !== 10 && value !== 20) {
            // models with older options get reseleted to option 10-deciles
            value = 10;
            setScoringPartition(10)
        }
        const selected = scoringPartitionOptions.find(option => option[0] === value);   
        const label = selected[1];
        if(scoringPartition !== label) {
            setScoringPartitionLabel(label);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scoringPartition]);
    
    useEffect(() => {
        if(!initialRender && loaded) {
          checkForChanges();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelName,
        modelDescription,
        restrictGeography,
        restrictAudienceId,
        scoringPartition,
        excludedFields
    ]
    );
    
    useEffect(() => {
        if(!initialRender && loaded) {
          checkForChanges();
          checkForMinPositive();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        positiveCount
    ]);

    // handle not-saved-yet complex filters
    useEffect(() => {
      if(!complexFields && session.crossfilterStore.isComplex) {
        setComplexFilters(session.crossfilterStore.complexFilters);
        session.crossfilterStore.getComplexFields().then((_complexFields) => {
            setComplexFields(_complexFields);
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [complexFields])


    function getModelNameForSave() {
        if (restrictAudienceName !== '(none)') {
            return restrictAudienceName;
        } else {
            return ''
        }
    }

    function setScoringPartitionValue(ev) {
        if (typeof(ev.target.value) !== "undefined") {
            setScoringPartition(ev.target.value)
        }
    }

    function saveModel(build) {
        setLoaded(false);
        let filters_to_store = session.crossfilterStore.getFiltersReportData();
        let filters_info_values = Object.values(session.chartsStore.charts);
        let selection_criteria_to_store = [];

        // remove any filter without values
        filters_to_store && filters_to_store.length && filters_to_store.forEach((item) => {
            if(item && Object.keys(item).length !== 0 && item.hasOwnProperty("filter_values")) {
            // add id and ds values
            let find_by_label = filters_info_values.find(_id => _id.label === item.filter_name);
            selection_criteria_to_store.push({...item, ds: find_by_label.ds, id: find_by_label.id});
            }
        })
        session.crossfilterStore.build_model(
            {
                id: (!!props.args.model) ? props.args.model : null,
                name: modelName,
                description: modelDescription,
                filters: activeFilters,
                restrict_geography: restrictGeography,
                build_model: build,
                excluded_fields: excludedFields,
                filter_model_id: restrictAudienceId,
                scoring_quantiles: scoringPartition,
                is_complex: isComplex,
                complex_filters: session.crossfilterStore.complexFilters,
                saved_selection_criteria: selection_criteria_to_store,
                filter_model_name: getModelNameForSave()
            },
            (response) => {
                targetModelId.current = response.id;
                session.crossfilterStore.setFilters({});
                unsavedContinuePath.current = '/model';
                history.push(`/model#model=${response.id}`)
                setLoaded(false)
                setRefreshKey(k => k + 1)
            },
            (error => {
                setLoaded(true)
                console.log(`Error: ${JSON.stringify(error)}`)
            })
        )
    }

    function checkForChanges() {
        if (!originalModel.name) {
            setModelHasChanged(true);
            return;
        }
        const nullAudienceID = (originalModel.filter_model_id === null) ? "" : originalModel.filter_model_id;
        const excludes = originalModel.excluded_fields;
        let excludeHasChanged = false;
        if (excludes.length === excludedFields.length) {
            excludes.forEach((item, index) => { 
                if (item.id !== excludedFields[index].id) {
                    excludeHasChanged = true;
                }
            });
        } else {
            excludeHasChanged = true;
        }
        const hasChanged = (
            modelName !== originalModel.name ||
            modelDescription !== originalModel.description ||
            restrictGeography !== originalModel.restrict_geography ||
            restrictAudienceId !== nullAudienceID ||
            scoringPartition.toString() !== originalModel.scoring_quantiles.toString() ||
            excludeHasChanged
        );       
        setModelHasChanged(hasChanged);
    }

    function checkForMinPositive() {
        const min = parseInt(minimumPositiveRecords);
        if(hasMinPositiveCount !== (positiveCount >= min)) {
          setHasMinPositiveCount(positiveCount >= min);
        }
    }

    function checkRequiredFields() {
        let retval = false;
        if (!modelName) {
            setModelNameError('Required')
            setModelNameInvalid(true);
        } else {
            setModelNameError('')
            setModelNameInvalid(false);
        }
        
        if (!modelDescription) {
            setModelDescriptionError('Required')
            setModelDescInvalid(true);
        } else {
            setModelDescriptionError('')
            setModelDescInvalid(false);
        }

        checkForMinPositive();

        retval = (modelName && modelDescription) ? true : false;
        return retval;
    }
    function onSave() {
        if (checkRequiredFields()) {
            saveModel(false);
        }
    }

    function onBuild() {
        if (checkRequiredFields()) {
            saveModel(true);
        }
    }

    function toggleRestrictGeography() {
        setRestrictGeography(!restrictGeography);
    }

    function selectRestrictAudience(event){
        //find the name from the options
        let name ='';
        let i=0;
        let elements = event.target.childElementCount;
        if (elements === 0) {
            name = event.target.innerHTML;
            setRestrictAudienceName(name);
        } else {
            while (i < elements) {
                if (event.target[i].value === event.target.value) {
                    name = event.target[i].label;
                    break;
                }
                i=i+1;
            }
            event.target[event.target.value] && setRestrictAudienceName(event.target[event.target.value].label);
        }
        if (name !== '(none)' ) {
            setAudienceSearchTerm('');
        }
        setRestrictAudienceId(event.target.value);
    }

    function getAudienceSearchTerm() {
        return searchTerm;
    }

    function setAudienceSearchTerm(value) {
        setSearchTerm(value);
    }

    function toggleModal(event){
        setComplexModalIsOpen(!complexModalIsOpen)
    }

    function toggleUnsavedModal(event) {
        //sets the continue path to null after viewing
        unsavedContinuePath.current = null;
        setUnsavedModalIsOpen(!unsavedModalIsOpen)
    }

    function toggleScoringPartitonsDropDown() {
        setScoringPartitionsIsOpen((prevState) => !prevState);
    }

    function toggleDescriptionExpand(e) {
        const desc = e.target;
        if (e.type === "focus") {
            desc.setAttribute("rows", 4);
        } else {
            desc.setAttribute("rows", 1);
        }
    }

    useEffect(() => {
      // check if the last saved scoring has changed
      if("scoring_quantiles" in originalModel) {
        if(scoringPartition.toString() !== originalModel.scoring_quantiles.toString()) {
          originalModel.scoring_quantiles = scoringPartition;
        } 
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded])

    useEffect(() => {
      setInitialRender(false);
    }, []);

    // To run after everything is actually loaded to check for missing features.
    useEffect(() => { 
        if (loaded && session.crossfilterStore.audienceLoaded && session.chartsStore.loaded) {
            let missing = false;
            let availableFilters;
            let charts = session.chartsStore.charts;
            charts = Object.entries(charts);
            if (originalModel.is_complex) {
                availableFilters = JSON.parse(JSON.stringify(originalModel.complex_filters));
                availableFilters = existingComplexFilters(originalModel.complex_filters, charts, availableFilters);
                missing = !isEqual(availableFilters.tree.children1, originalModel.complex_filters.tree.children1);
                if (!noFilters) {
                    setNoFilters(availableFilters && !Object.keys(availableFilters.tree.children1).length);
                    setDataFiltersHaveChanged(missing);
                    return;
                }
            } else {
                if (!originalModel.filters) return;
                availableFilters = existingSimpleFilters(originalModel.filters, charts, availableFilters);
                missing = !isEqual(availableFilters, Object.entries(originalModel.filters));
                if (!noFilters) {
                    const no_filters = (availableFilters && !Object.keys(availableFilters).length) ? true : false;
                    setNoFilters(no_filters);
                    setDataFiltersHaveChanged(missing);
                    return;
                }
            }
        } else {
            setNoFilters(false);
            setDataFiltersHaveChanged(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loaded, session.crossfilterStore.loaded, session.crossfilterStore.audienceLoaded, session.chartsStore.loaded]);

    useEffect(() => {
        if (!loaded) return;
        if (restrictedAudienceMissing) {
            setRestrictAudienceId(null);
            setRestrictAudienceName("");
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    },[restrictedAudienceMissing])

    // Check that data fields that are selected still exist.
    function checkDataFieldsExist(_fields) {
        if (!_fields.length) return false;
        let fieldExists;
        fieldExists = allFields.filter((a) => (_fields || []).find((f) => (a.ds === f.ds && a.id === f.id)));
        const missingFields = (fieldExists.length !== _fields.length);
        return missingFields;
    }

    useEffect(() => {
      // keep global sync with local
      setIsGlobalStateSaved(!modelHasChanged);
      return () => {
        // when leaving component reset global
        setIsGlobalStateSaved(true);
      };
    // eslint-disable-next-line
    }, [modelHasChanged])
    
    useEffect(() => {
      if(currentCompanyId && (session.appState.activeCompany.id !== currentCompanyId)) {
        setModelHasChanged(false);
      }
    // eslint-disable-next-line
    }, [session.appState.activeCompany.id])

    useEffect(() => {
      setCurrentCompanyId(session.appState.activeCompany.id);
    // eslint-disable-next-line
    }, [])

    return (
        <div className="page">
            <div className="list-header sticky-page-header">
                <Link to="/models">
                <button type="button" className="new-button">
                    <i className="bi bi-chevron-left"></i> Models
                </button>
                </Link>
                <h1>Model Audience</h1>
                <i className="spacer" />
                <div className="model-actions header-actions">
                    {access('model_edit') &&
                        <Button
                            className="new-button promote"
                            type="button"
                            disabled={(!modelHasChanged && !dataFiltersHaveChanged && !restrictedAudienceMissing ) || !loaded }
                            onClick={onSave}>
                            {(modelHasChanged || dataFiltersHaveChanged || restrictedAudienceMissing) ? "Save Model" : "Model Saved"}
                        </Button>
                    }
                    {access('model_build') &&
                        <Button
                            className="new-button promote"
                            type="button"
                            disabled={modelHasChanged || !hasMinPositiveCount || dataFiltersHaveChanged || restrictedAudienceMissing || !loaded }
                            onClick={onBuild}>
                            Build Model
                        </Button>
                    }
                </div>
            </div>
            <div className='page model-campaign-page'>
                {!loaded && <div className='overlay'>
                    <div className='loader' />
                </div>}

                <section id="place-model" className='main-content'>

                        <div id="model">
                            <Card>
                                <CardHeader>                
                                    <Form className='bs-form model-form model-name-desc' onSubmit={e => e.preventDefault()}>
                                        <Row>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label for="model-name">Model Name:<sup>*</sup></Label>
                                                    <Input
                                                        id="model-name"
                                                        type="text"
                                                        onChange={onModelNameChange}
                                                        value={modelName}
                                                        invalid={modelNameInvalid}
                                                        disabled={!access('model_edit')}
                                                        />
                                                    <FormFeedback invalid={modelNameInvalid.toString()}>
                                                        {modelNameError}
                                                    </FormFeedback>
                                                </FormGroup>
                                            </Col>
                                            <Col md={8}>
                                                <FormGroup>
                                                    <Label for="model-description">Description:<sup>*</sup></Label>
                                                    <Input
                                                        id="model-description"
                                                        type="textarea"
                                                        rows="1"
                                                        onChange={onModelDescriptionChange}
                                                        value={modelDescription}
                                                        invalid={modelDescInvalid}
                                                        onFocus={toggleDescriptionExpand}
                                                        onBlur={toggleDescriptionExpand}
                                                        disabled={!access('model_edit')}
                                                    />
                                                    <FormFeedback invalid={modelDescInvalid.toString()}>
                                                        {modelDescriptionError}
                                                    </FormFeedback>
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        </Form>
                                </CardHeader>
                                <CardBody>
                                    <div id="model-restrictions">
                                        <h2>Model Settings</h2>
                                        <Form className='bs-form model-form restrictions'>
                                            <FormGroup row className='form-grp'>
                                                <Label for="scoring-partitions">Scoring Partitions:</Label>
                                                    <Dropdown
                                                        className="model-partitions"
                                                        isOpen={scoringPartitionsIsOpen}
                                                        toggle={toggleScoringPartitonsDropDown}
                                                        id="scoring-partitions"
                                                        disabled={!access('model_edit')}
                                                    >
                                                        <DropdownToggle size="sm" caret color="light">
                                                        {scoringPartitionLabel}
                                                        </DropdownToggle>
                                                        <DropdownMenu value={scoringPartition}>
                                                        {scoringPartitionOptions.map((l, i) => (
                                                            <DropdownItem
                                                            onClick={(e) => {
                                                                setScoringPartitionValue(e);
                                                            }}
                                                            key={i.toString()}
                                                            value={l[0]}
                                                            >
                                                            {l[1]}
                                                            </DropdownItem>
                                                        ))}
                                                        </DropdownMenu>
                                                    </Dropdown>
                                            </FormGroup>
                                            <Alert
                                                isOpen={restrictedAudienceMissing}
                                                color="warning"
                                                className="d-flex"
                                                >
                                                <i className="bi bi-exclamation-triangle-fill"></i>
                                                <div>
                                                    Since the last time this model was saved, the restricted audience below is no longer available. <br /><strong>Please choose another audience or save this order.</strong>
                                                </div>
                                                </Alert>
                                            <FormGroup className='form-grp audience-restrict'>
                                                <Col sm={7}>
                                                    <Label>Restrict model to people in this saved audience (top 20 shown):</Label>
                                                    <SearchDropDown onSelect={selectRestrictAudience}
                                                                    selectedId={restrictAudienceId}
                                                                    queryByName='audience/queryByName'
                                                                    idName='id'
                                                                    valueName='name'
                                                                    defaultId='-1'
                                                                    defaultValue=''
                                                                    defaultLabel='(none)'
                                                                    selectedName={restrictAudienceName}
                                                                    setSearchTerm={setAudienceSearchTerm}
                                                                    getSearchTerm={getAudienceSearchTerm}
                                                                    searchTerm={searchTerm}
                                                                    pageSize='20'
                                                                    disabled={!access('model_edit')}
                                                                    />
                                                </Col>
                                            </FormGroup>
                                            <FormGroup check className='form-grp'>
                                                <Input
                                                    id="geography"
                                                    name="check"
                                                    type="checkbox"
                                                    onChange={toggleRestrictGeography}
                                                    disabled={!access('model_edit')}
                                                />
                                                <Label check for="geography" className="pt-1">
                                                    Restrict training data to input geography
                                                </Label>
                                                &nbsp;
                                                <InfoPopover 
                                                    title="Restrict Training Data Geography"
                                                    message="Selecting this option limits the model's training data to postal codes in your target audience. This is useful when focusing on a specific region as opposed to the entire U.S., as it helps the model prioritize location-specific attributes.<br /><br /> <b>Note</b>: Make sure your target audience includes all relevant zip codes or geographies for your marketing strategy before using this setting. Contact Customer Success for questions or additional details."
                                                    offsetTop={0}
                                                />
                                            </FormGroup>
                                        </Form>
                                        <Form className='bs-form model-form exclusions'>
                                            <FormGroup className='form-grp'>
                                            <Label>Exclude input variables:</Label>
                                            <Alert
                                                isOpen={dataFieldsMissing}
                                                color="warning"
                                                className="d-flex"
                                                >
                                                <i className="bi bi-exclamation-triangle-fill"></i>
                                                <div>
                                                    Since the last time this model was saved, one or more of the exclude input variable data fields below are no longer available.<br /><strong>Please review the exclusion input data fields below prior to building or scoring your model.</strong>
                                                </div>
                                                </Alert>
                                                <DualSelect
                                                            optionLabel='label'
                                                            allOptions={allFields}
                                                            selectedOptions={excludedFields}
                                                            setSelectedOptions={setExcludedFields}
                                                            disabled={!access('model_edit')}
                                                            />
                                            </FormGroup>
                                        </Form>
                                    </div>
                                </CardBody>
                            </Card>
                        <div>
                            <Modal centered backdrop
                                size={"lg"}
                                isOpen={complexModalIsOpen}
                                toggle={() => toggleModal()}
                            >
                                <ModalHeader toggle={() => toggleModal()}>
                                    Complex Filters
                                </ModalHeader>
                                <ModalBody>
                                    <ComplexFilterBuilder
                                        initialQuery = { isComplex && complexFilters && complexFilters.tree ? complexFilters.tree : null }
                                        fields={complexFields}
                                        readonly={true}
                                    />
                                </ModalBody>
                                <ModalFooter>
                                    <Button onClick={toggleModal}>Close</Button>
                                </ModalFooter>
                            </Modal>
                            <Modal centered backdrop
                                isOpen={noFilters}
                            >
                                <ModalHeader>
                                <i className="bi bi-exclamation-triangle-fill"></i>
                                    No Filters in this Model
                                </ModalHeader>
                                <ModalBody>
                                    Since the last time this model was saved, the filters used are no longer available. Please open the <strong>Explorer</strong> page and create a new model.
                                </ModalBody>
                                <ModalFooter>
                                    <Button onClick={(e) => {history.push("/models")}}>Back to Models</Button>
                                    <Button className="promote" onClick={(e) => {history.push("/explorer")}}>Open Explorer</Button>
                                </ModalFooter>
                            </Modal>
                        </div>
                    </div>
                </section>


                    <section id="right-sidebar" className='model-overview'>
                        <div id="audience-summary">
                            <h2>Target Audience Summary</h2>
                            <div id="summary">
                                <div id="criteria">
                                <h3>Selection Criteria</h3>
                                <Alert
                                    isOpen={dataFiltersHaveChanged && !noFilters}
                                    color="warning"
                                    className="d-flex"
                                    >
                                    <i className="bi bi-exclamation-triangle-fill"></i>
                                    <div>
                                        Since the last time this model was saved, one or more of the filters chosen are no longer available. <strong>Please review the selection criteria below prior to building and scoring your model</strong>.
                                    </div>
                                </Alert>
                                    {activeFilters.map((chart_id) =>
                                        <Chart summarize chart_id={chart_id} key={chart_id} viewOnly originalFilters={originalModel.filters} />
                                )}
                                {noFilters && <Card className="p-3">No selected criteria found.</Card>}
                                {isComplex &&
                                    <div className='center'>
                                        <Button onClick={toggleModal}>Complex Filters</Button>
                                    </div>
                                }
                                </div>
                            </div>
                        </div>
                        {(positiveCount > -1 || negativeCount > -1 || scoringUniverse > -1) && (
                            <>
                            <h2>Model Summary</h2>
                        <Card>
                            <div className="labeled-items vertical">
                                {positiveCount > -1 && (
                                <div className="labeled-item">
                                    <label>Positive Records Available:</label>
                                    <div className="value">
                                    {formatNumber(positiveCount)}
                                    </div>
                                    {positiveCount < 1000 && hasMinPositiveCount && (
                                    <Alert color="warning">
                                        <div><i className="bi bi-lightbulb"></i>For best results, at least <strong>1000</strong> positive records are needed for training. This model currently has <strong>{positiveCount}</strong>.</div>
                                    </Alert>
                                    )}
                                    {!hasMinPositiveCount && (
                                    <Alert id="minPosCountWarning" color="danger">
                                        <div><i className="bi bi-exclamation-triangle-fill"></i>
                                            At least <strong>{minimumPositiveRecords}</strong> positive records are required to successfully build a model. This model currently has <strong>{positiveCount}</strong>.
                                        </div>
                                        <br />
                                        <div><i className="bi bi-lightbulb"></i>
                                            Consider starting with an audience of a larger audience size, or edit the restriction settings for the model and click the "Save" button again to re-calculate positive records.
                                        </div>
                                    </Alert>
                                    )}
                                </div>
                                    )}
                                    {negativeCount > -1 && (
                                <div className="labeled-item">
                                    <label>Negative Records Available:</label>
                                    <div className="value">
                                    {formatNumber(negativeCount)}
                                    </div>
                                </div>
                                )}
                                {scoringUniverse > -1 && (
                                    <div className="labeled-item">
                                        <label>Universe of Records</label>
                                        <div className="value">
                                            {formatNumber(scoringUniverse)}
                                        </div>
                                    </div>)}
                            </div>
                    </Card>
                        </>
                        )}
                </section>
            </div>
            <Modal centered backdrop
                isOpen={unsavedModalIsOpen && !noFilters}
                toggle={() => toggleUnsavedModal()}
            >
                <ModalHeader toggle={() => toggleUnsavedModal()}>
                    Unsaved Model
                </ModalHeader>
                <ModalBody>
                    You have unsaved changes to this model. If you leave the page before saving, your changes will be lost. 
                </ModalBody>
                <ModalFooter>
                    <Button className="promote" onClick={toggleUnsavedModal}>Cancel</Button>
                    <Button onClick={continueWithoutSaving}>Continue</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
